import { ROUTE_ACTIONS, type RouteAction } from '@/app/router/routes'
import { EnumTypeStates } from '@/features/states/models/state.schema'
import { getViewSteps } from '../consts/routes'

/**
 * Cuándo el expediente tiene consola de la app del cliente. Lógica pura, sin React ni red.
 *
 * ── ☠️ POR QUÉ NO ES `isRescindOrEnd` ────────────────────────────────────────
 *
 * El booleano del store mete en la misma bolsa la venta APROBADA y la RESCINDIDA, y para esta
 * pregunta son opuestas: **rescindir deja `property` en `null`**, así que en una rescindida no hay
 * inmueble al que dar acceso ni pantalla que previsualizar. Usarlo acá abriría la consola
 * exactamente sobre los expedientes que no tienen nada que mostrar.
 *
 * Por eso la regla mira el tipo terminal CONCRETO —`end`— y no el booleano derivado. Es el mismo
 * predicado que la tarjeta «Acceso en la app» venía escribiendo a mano en su propio archivo desde
 * el ADR 0024; acá deja de estar duplicado, que era la forma de que una de las dos superficies se
 * corriera sin que la otra se enterara.
 *
 * ── LAS TRES CONDICIONES, Y POR QUÉ LAS TRES ─────────────────────────────────
 *
 * | Condición | Qué pasa sin ella |
 * | --- | --- |
 * | `action === view` | En editar, lo que hay en pantalla todavía no es lo que el cliente tiene |
 * | La venta está CERRADA | Cerrar la venta es lo que habilita la preparación posventa (doc §1) |
 * | Hay `publicId` | Los endpoints v2 del preview y del acceso piden el `neg_`, no el `_id` de v1 |
 *
 * Las tres se evalúan sobre datos que el expediente YA cargó, así que no hay una lectura extra ni
 * un estado intermedio: cuando el store está hidratado, la respuesta es definitiva.
 */
type ClientAppEligibilityInput = {
  /** El estado vigente tal cual lo guarda el store del expediente. */
  currentState?: { state?: { type?: EnumTypeStates } | null } | null
  /** El `neg_` de la negociación. */
  publicId?: string | null
  /** La acción de la URL (`view` / `edit` / `create`). */
  action?: RouteAction | null
}

/**
 * La venta está CERRADA: `type === 'end'`. Ni rescindida, ni inactiva, ni en proceso.
 *
 * No se exporta: la única pregunta que las pantallas hacen es la de abajo, y publicar la mitad
 * invita a que alguien la use sola y se saltee el `publicId` o la acción. Sus casos se cubren
 * desde `isClientAppEligible`, que es la puerta real.
 */
function isNegotiationClosed(currentState: ClientAppEligibilityInput['currentState']): boolean {
  return currentState?.state?.type === EnumTypeStates.end
}

/**
 * ¿Este expediente tiene consola de la app del cliente? Gobierna la entrada de la subnav, el
 * registro de la ruta y la tarjeta de acceso: una sola respuesta para las tres, o la pestaña
 * aparece donde la ruta no existe.
 */
export function isClientAppEligible({
  currentState,
  publicId,
  action
}: ClientAppEligibilityInput): boolean {
  return action === ROUTE_ACTIONS.view && isNegotiationClosed(currentState) && Boolean(publicId)
}

/**
 * Los pasos del modo VER de ESTE expediente: la secuencia sin «Vista móvil» salvo que el expediente
 * sea elegible. Es el único lugar que junta la elegibilidad con el recorrido; el header, el store,
 * el router y la subnav lo leen de acá.
 */
export function getNegotiationViewSteps(input: ClientAppEligibilityInput): string[] {
  return getViewSteps({ eligible: isClientAppEligible(input) })
}
